import { allOf, signalEquals } from './equation.js';
import { RUNTIME_EVENTS, SCRATCHPAD_EVENTS } from './graph.js';
import { startConversationOpening } from './template-openings.js';
/**
 * The showing scheduler: call a prospect who already knows the home and help
 * them book a showing. The home is named in the greeting and the prompt; the
 * only open-ended part is picking a time. The agent cannot transfer the call,
 * so a request for a person goes to its own node, which takes a message.
 */
const START_ID = '00000000-0000-4000-8000-00000007d001';
const GREET_ID = '00000000-0000-4000-8000-00000007a001';
const SHOWING_ID = '00000000-0000-4000-8000-00000007c001';
const MESSAGE_ID = '00000000-0000-4000-8000-00000007c002';
const END_BOOKED_ID = '00000000-0000-4000-8000-00000007a002';
const END_CLOSE_ID = '00000000-0000-4000-8000-00000007a003';
const END_DNC_ID = '00000000-0000-4000-8000-00000007a004';
const edgeId = (n) => `00000000-0000-4000-8000-00000007e${String(n).padStart(3, '0')}`;
const OPENING = startConversationOpening({
    startNodeId: START_ID,
    entryEdgeId: edgeId(1),
    exitEdgeId: edgeId(10),
    toNodeId: GREET_ID,
});
export const SHOWING_SCHEDULER_GRAPH_TEMPLATE = {
    name: 'Showing scheduler',
    persona: 'You are Sam, a friendly assistant for Super. One or two short spoken ' +
        'sentences per turn.',
    active: true,
    knowledgeBaseIds: [],
    watchEvents: [
        SCRATCHPAD_EVENTS.DO_NOT_CALL,
        SCRATCHPAD_EVENTS.NOT_INTERESTED,
        SCRATCHPAD_EVENTS.HANDOFF_REQUESTED,
    ],
    nodes: [
        OPENING.node,
        {
            id: GREET_ID,
            type: 'say',
            title: 'Greeting',
            position: 1,
            text: "Hi {{firstName}}, this is Sam with Super! I'm reaching out to help " +
                'you schedule a showing for 300 West Riverside Drive, unit 1504.',
        },
        {
            id: SHOWING_ID,
            type: 'conversation',
            title: 'Book a showing',
            position: 2,
            prompt: 'Open by inviting the caller to begin — e.g. "Can I get this ' +
                'started for you?" Then help the tenant schedule a showing for ' +
                '300 West Riverside Drive, unit 1504. Find a time that works for ' +
                'them, and book the time they pick.',
            tools: [
                { tool: 'TENANT_SHOWING_SCHEDULING' },
                { tool: 'SWITCH_LANGUAGE' },
            ],
            knowledgeBaseIds: [],
            excludedKnowledgeBaseIds: [],
            speaksOnEntry: true,
            signals: [
                {
                    id: 'declines_showing',
                    text: 'The caller declines to book a showing, or indicates they are ' +
                        'ready to wrap up the conversation.',
                },
            ],
        },
        {
            id: MESSAGE_ID,
            type: 'conversation',
            title: 'Take a message',
            position: 3,
            prompt: 'Say you cannot transfer them right now, but you can take a ' +
                'message for the team to follow up. Take down their message and ' +
                'read it back briefly. Then ask: "Is there anything else I can help ' +
                'with?"',
            tools: [],
            knowledgeBaseIds: [],
            excludedKnowledgeBaseIds: [],
            signals: [
                {
                    id: 'message_taken',
                    text: 'The caller gave their message for the team and said there is ' +
                        'nothing else',
                },
            ],
        },
        {
            id: END_BOOKED_ID,
            type: 'end_conversation',
            title: 'End conversation',
            position: 4,
            signOff: {
                mode: 'exact',
                promptText: '',
                exactText: "You're all set! We can't wait for you to see the home. Have a " +
                    'wonderful day!',
            },
        },
        {
            id: END_CLOSE_ID,
            type: 'end_conversation',
            title: 'End conversation',
            position: 5,
            signOff: {
                mode: 'exact',
                promptText: '',
                exactText: "No problem at all! If you'd like to see it another time, we're " +
                    'happy to help. Have a great day!',
            },
        },
        {
            id: END_DNC_ID,
            type: 'end_conversation',
            title: 'End conversation',
            position: 6,
            signOff: {
                mode: 'exact',
                promptText: '',
                exactText: "Understood. I'll update your contact so you don't get more calls " +
                    'from us. Have a good day.',
            },
        },
    ],
    edges: [
        OPENING.edges[0],
        // Cross-cutting flows, in priority order.
        {
            id: edgeId(3),
            fromNodeId: null,
            toNodeId: END_DNC_ID,
            condition: { type: 'event', event: SCRATCHPAD_EVENTS.DO_NOT_CALL },
            position: 1,
        },
        {
            id: edgeId(4),
            fromNodeId: null,
            toNodeId: MESSAGE_ID,
            condition: { type: 'event', event: SCRATCHPAD_EVENTS.HANDOFF_REQUESTED },
            position: 2,
        },
        {
            id: edgeId(6),
            fromNodeId: null,
            toNodeId: END_CLOSE_ID,
            condition: { type: 'event', event: SCRATCHPAD_EVENTS.NOT_INTERESTED },
            position: 3,
        },
        { ...OPENING.edges[1], position: 4 },
        {
            id: edgeId(2),
            fromNodeId: GREET_ID,
            toNodeId: SHOWING_ID,
            condition: { type: 'event', event: RUNTIME_EVENTS.TEXT_DELIVERED },
            position: 5,
        },
        // A booking wins the turn over a decline read in the same turn.
        {
            id: edgeId(5),
            fromNodeId: SHOWING_ID,
            toNodeId: END_BOOKED_ID,
            condition: { type: 'event', event: 'tour_scheduled' },
            position: 6,
        },
        {
            id: edgeId(7),
            fromNodeId: SHOWING_ID,
            toNodeId: END_CLOSE_ID,
            condition: allOf(signalEquals(SHOWING_ID, 'declines_showing', true)),
            position: 7,
        },
        {
            id: edgeId(9),
            fromNodeId: MESSAGE_ID,
            toNodeId: END_CLOSE_ID,
            condition: allOf(signalEquals(MESSAGE_ID, 'message_taken', true)),
            position: 8,
        },
    ],
};
//# sourceMappingURL=showing-scheduler-template.js.map